import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@/lib/zodcompat";
import { z } from "zod";
import { Dialog } from "@base-ui/react/dialog";
import { toast } from "sonner";
import { MoneyInput } from "@/components/form/money-input";
import { PercentInput } from "@/components/form/percent-input";
import { Button } from "@/components/ui/button";
import { LoadingButton } from "@/components/ui/loading-button";
import { useUpdateFreightFinance } from "@/hooks/useFreights";

const financeSchema = z.object({
  freightValue: z.preprocess(
    (v) => (v === "" || v == null ? undefined : v),
    z.coerce.number().positive("Valor do frete deve ser positivo").optional(),
  ),
  driverValue: z.preprocess(
    (v) => (v === "" || v == null ? undefined : v),
    z.coerce.number().positive("Valor do motorista deve ser positivo").optional(),
  ),
  advancePercent: z.coerce.number().min(0).max(100).optional(),
  notes: z.string().max(500).optional(),
});

type FinanceFormData = z.infer<typeof financeSchema>;

interface EditFreightFinanceModalProps {
  freightId: string;
  initialFreightValue: number | null;
  initialDriverValue: number | null;
  initialAdvancePercent: number | null;
  initialNotes: string | null;
  driverFieldsLocked?: boolean;
  open: boolean;
  onClose: () => void;
}

export function EditFreightFinanceModal({
  freightId,
  initialFreightValue,
  initialDriverValue,
  initialAdvancePercent,
  initialNotes,
  driverFieldsLocked = false,
  open,
  onClose,
}: EditFreightFinanceModalProps) {
  const { mutate, isPending } = useUpdateFreightFinance(freightId);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FinanceFormData>({
    resolver: zodResolver(financeSchema),
  });

  useEffect(() => {
    if (open) {
      reset({
        freightValue: initialFreightValue ?? (undefined as unknown as number),
        driverValue: initialDriverValue ?? (undefined as unknown as number),
        advancePercent: initialAdvancePercent ?? 70,
        notes: initialNotes ?? "",
      });
    }
  }, [open, reset, initialFreightValue, initialDriverValue, initialAdvancePercent, initialNotes]);

  function onSubmit(data: FinanceFormData) {
    const payload = driverFieldsLocked
      ? { freightValue: data.freightValue, notes: data.notes }
      : data;
    mutate(payload, {
      onSuccess: () => {
        toast.success("Valores atualizados!");
        onClose();
      },
      onError: (err: unknown) => {
        const msg = (err as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message;
        toast.error(msg ?? "Erro ao atualizar valores");
      },
    });
  }

  return (
    <Dialog.Root open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Backdrop className="fixed inset-0 z-40 bg-black/50" />
        <Dialog.Popup className="fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-background p-6 shadow-md">
          <Dialog.Title className="text-lg font-semibold text-foreground">
            Editar valores do frete
          </Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-muted-foreground">
            Atualize valores e percentual de adiantamento.
          </Dialog.Description>

          {driverFieldsLocked && (
            <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              Repasse já pago — só o valor do frete e as observações podem ser
              alterados. Valor do motorista e % adiantamento estão travados.
            </p>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="mt-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">
                  Valor do frete
                </label>
                <MoneyInput
                  {...register("freightValue")}
                  error={!!errors.freightValue}
                  placeholder="0,00"
                />
                {errors.freightValue && (
                  <p className="text-xs text-destructive">{errors.freightValue.message}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">
                  Valor do motorista
                </label>
                <MoneyInput
                  {...register("driverValue")}
                  error={!!errors.driverValue}
                  placeholder="0,00"
                  disabled={driverFieldsLocked}
                />
                {errors.driverValue && (
                  <p className="text-xs text-destructive">{errors.driverValue.message}</p>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">
                % Adiantamento
              </label>
              <PercentInput
                {...register("advancePercent")}
                error={!!errors.advancePercent}
                disabled={driverFieldsLocked}
              />
              {errors.advancePercent && (
                <p className="text-xs text-destructive">{errors.advancePercent.message}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">
                Observações
              </label>
              <textarea
                rows={3}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
                {...register("notes")}
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isPending}
              >
                Cancelar
              </Button>
              <LoadingButton
                type="submit"
                loading={isPending}
                loadingText="Salvando…"
              >
                Salvar
              </LoadingButton>
            </div>
          </form>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
